import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  LayoutDashboard,
  Upload,
  FileText,
  Newspaper,
  Globe,
  Wrench,
  Building2,
  Workflow,
  Lightbulb,
  ListChecks,
  Image as ImageIcon,
  Share2,
  Link2,
  Mail,
  Megaphone,
  Target,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User as UserIcon,
  Users,
  UserPlus,
  ShieldAlert,
  GitBranch,
  Sun,
  Moon,
  Snowflake,
} from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile.js';
import { WebSocketProvider } from '../contexts/WebSocketContext.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { ProviderHealthBar } from './ProviderHealthBar.js';
import { DependencyAlertChips } from './DependencyAlertChips.js';
import { useQueryClient } from '@tanstack/react-query';

interface NavItem {
  to: string;
  icon: React.ReactElement;
  label: string;
  /** Roles that may see this nav item — visible if the operator has ANY of
   *  them. `super_admin` is the implicit superset and always passes (via
   *  useAuth().hasRole), so it need not be listed. */
  roles: string[];
  /** Sidebar section heading this item sits under. */
  group: 'Content' | 'Outbound' | 'System';
}

const NAV_ITEMS: NavItem[] = [
  // Order = how often an operator needs it, and how fast (often from a phone):
  // time-sensitive content decisions first, then outbound and insight, then
  // data, backend and admin surfaces. Items of a group must stay contiguous —
  // a heading renders wherever the group changes.
  { to: '/dashboard',            icon: <LayoutDashboard className="h-6 w-6" />, label: 'Dashboard',     roles: ['sales'], group: 'Content' },
  { to: '/review-queue',         icon: <ShieldAlert className="h-6 w-6" />,     label: 'Review Queue',  roles: ['social_manager', 'sales', 'data_manager'], group: 'Content' },
  { to: '/announcements',        icon: <FileText className="h-6 w-6" />,        label: 'Announcements', roles: ['sales', 'data_manager'], group: 'Content' },
  { to: '/articles',             icon: <Newspaper className="h-6 w-6" />,       label: 'Articles',      roles: ['sales', 'data_manager'], group: 'Content' },
  { to: '/social',               icon: <Share2 className="h-6 w-6" />,          label: 'Social',        roles: ['social_manager', 'sales'], group: 'Content' },
  { to: '/analytics',            icon: <BarChart3 className="h-6 w-6" />,       label: 'Analytics',     roles: ['sales'], group: 'Content' },
  { to: '/reconciliation',       icon: <GitBranch className="h-6 w-6" />,       label: 'Story Review',  roles: ['sales'], group: 'Content' },
  { to: '/triage',               icon: <ListChecks className="h-6 w-6" />,      label: 'Triage',        roles: ['super_admin'], group: 'Content' },
  { to: '/upload',               icon: <Upload className="h-6 w-6" />,          label: 'Upload',        roles: ['sales'], group: 'Content' },
  { to: '/notifications/queue',  icon: <Mail className="h-6 w-6" />,            label: 'Notifications', roles: ['sales'], group: 'Outbound' },
  { to: '/notifications/subscribers', icon: <UserPlus className="h-6 w-6" />,   label: 'Subscribers',   roles: ['sales'], group: 'Outbound' },
  { to: '/insights',             icon: <Target className="h-6 w-6" />,          label: 'Insights',      roles: ['sales'], group: 'Outbound' },
  { to: '/cooloff',              icon: <Snowflake className="h-6 w-6" />,       label: 'Cool-off',      roles: ['sales'], group: 'Outbound' },
  { to: '/campaigns',            icon: <Megaphone className="h-6 w-6" />,       label: 'Campaigns',     roles: ['sales'], group: 'Outbound' },
  { to: '/intelligence',         icon: <Lightbulb className="h-6 w-6" />,       label: 'Intelligence',  roles: ['sales'], group: 'Outbound' },
  { to: '/entities',             icon: <Building2 className="h-6 w-6" />,       label: 'Entities',      roles: ['data_manager'], group: 'System' },
  { to: '/images',               icon: <ImageIcon className="h-6 w-6" />,       label: 'Images',        roles: ['super_admin'], group: 'System' },
  { to: '/publishing',           icon: <Globe className="h-6 w-6" />,           label: 'Publishing',    roles: ['super_admin'], group: 'System' },
  { to: '/pipeline',             icon: <Workflow className="h-6 w-6" />,        label: 'Pipeline',      roles: ['super_admin'], group: 'System' },
  { to: '/tools',                icon: <Wrench className="h-6 w-6" />,          label: 'Tools',         roles: ['super_admin'], group: 'System' },
  { to: '/settings/linkedin',    icon: <Link2 className="h-6 w-6" />,           label: 'LinkedIn',      roles: ['super_admin'], group: 'System' },
  { to: '/admin/operators',      icon: <Users className="h-6 w-6" />,           label: 'Operators',     roles: ['super_admin'], group: 'System' },
];

function LayoutContent() {
  const { user, operator, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Persistent subscription — keeps ['announcements'] cache fresh on all pages
  useWebSocket({
    channels: ['assayerai:announcements'],
    autoInvalidate: true,
  });

  // Dependency episodes (OPERATOR_ALERTING_PLAN Phase 1). The context's
  // auto-invalidate keys on the channel's second segment — 'alerts' — which
  // nothing queries, so the dashboard query is invalidated explicitly here.
  const queryClient = useQueryClient();
  useWebSocket({
    channels: ['assayerai:alerts'],
    onEvent: {
      alert_opened: () => queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      alert_closed: () => queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    },
  });

  // Click-away handler for the user-menu popover.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const visibleItems = NAV_ITEMS.filter((item) => item.roles.some((r) => hasRole(r)));
  const displayName = operator?.display_name?.trim() || user?.email || '';

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      {/* bg-header/hover:bg-header-hover are the FIXED brand navies — the
          header is the same in both themes. Do not use primary-800/900 here:
          those now theme-flip so they can serve as readable text tones. */}
      {/* pt-[env(safe-area-inset-top)]: as an installed web app the page
          draws under the iOS status bar (black-translucent), so the navy
          extends behind the clock and the controls start below it. Zero in
          a normal browser tab. */}
      <header className="bg-header border-b border-grey-600 pt-[env(safe-area-inset-top)]">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="mr-2 -ml-2 p-2 text-white hover:text-grey-100 touch-target"
                  aria-label="Open navigation"
                >
                  <Menu className="h-6 w-6" />
                </button>
              )}
              {/* Assayer Network A-mark (brand site alignment 2026-07-28) */}
              <img src="/assayer-amark-white.png" alt="" className="h-7 w-auto" />
              <span className="ml-3 text-xl font-semibold text-white">Assayer</span>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              {/* Backend 0.3.141 (CS250) answers 403 to an operator with no roles on
                  everything but /operator/me, so don't poll for them (/no-access). */}
              {(operator?.roles.length ?? 0) > 0 && <ProviderHealthBar />}
              {hasRole('super_admin') && <DependencyAlertChips />}

              <ThemeToggle />

              {/* User menu (custom popover — no shadcn DropdownMenu primitive) */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 text-white text-sm px-2 py-1 rounded hover:bg-header-hover transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <span className="max-w-[72px] min-[400px]:max-w-[96px] sm:max-w-[200px] truncate">{displayName}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-64 bg-surface rounded-lg shadow-lg border border-grey-200 py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-grey-200">
                      <p className="text-sm font-medium text-grey-700 truncate">
                        {operator?.display_name || 'Signed in'}
                      </p>
                      <p className="text-xs text-grey-500 truncate">{user?.email}</p>
                      {operator && operator.roles.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {operator.roles.map((r) => (
                            <span
                              key={r}
                              className="px-1.5 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded"
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-grey-700 hover:bg-grey-50"
                      role="menuitem"
                    >
                      <UserIcon className="h-4 w-4" />
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-grey-700 hover:bg-grey-50"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100dvh-4rem)]">
        {/* Sidebar */}
        <nav
          className={`w-64 bg-surface border-r border-grey-200 flex flex-col ${
            isMobile ? 'fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-in-out mt-[calc(4rem+env(safe-area-inset-top))]' : ''
          } ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}`}
        >
          {isMobile && (
            <div className="flex justify-end p-4 border-b border-grey-200">
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-grey-600 hover:text-grey-900"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          )}

          <div className="flex-1 p-6 space-y-2 overflow-y-auto">
            {visibleItems.map((item, i) => (
              <div key={item.to}>
                {item.group !== visibleItems[i - 1]?.group && (
                  <p className={`px-4 pb-1 text-xs font-semibold uppercase tracking-wider text-grey-500 ${i > 0 ? 'pt-4' : ''}`}>
                    {item.group}
                  </p>
                )}
                <NavLink
                  to={item.to}
                  icon={item.icon}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  {item.label}
                </NavLink>
              </div>
            ))}
          </div>
        </nav>

        {/* Mobile Overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 mt-[calc(4rem+env(safe-area-inset-top))]"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export function Layout() {
  return (
    <WebSocketProvider>
      <LayoutContent />
    </WebSocketProvider>
  );
}

function NavLink({
  to,
  icon,
  children,
  onClick,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-semibold transition-all ${
        isActive
          ? 'bg-primary-50 text-primary-700 border border-primary-200'
          : 'text-grey-700 hover:bg-grey-100 hover:text-grey-900'
      }`}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}

// Header theme switch. The pre-paint script in index.html applies the saved
// (or system) preference before first render; this button only flips the
// class and persists the choice. The whole app follows via the CSS-variable
// palette in index.css — no per-page dark: variants.
function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    try { localStorage.setItem('assayer-theme', next ? 'dark' : 'light'); } catch { /* storage unavailable */ }
    setDark(next);
  };
  return (
    <button
      onClick={toggle}
      className="p-2 rounded text-white hover:bg-header-hover transition-colors touch-target"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}
